import { Anchor } from "@tento-ui/components/anchor";
import { ContextMenu } from "@tento-ui/components/context-menu";
import { EditableText } from "@tento-ui/components/editable-text";
import { Table } from "@tento-ui/components/table/table";
import { TaskPresentation, TaskRowItem } from "@tento-lona/sheets-ui/platform";
import { TaskDateDisplay } from "./task-date-display";
import { TaskPriorityIndicator } from "./task-priority-indicator";
import { TaskStatusIndicator } from "./task-status-indicator";
import { NameMenuId, StatusMenuId } from "./task-table";
import { DragTaskState } from "../drag-task-state";
import { Prompt } from "@tento-ui/components/prompt";
import { DateTime, NaiveDateTime, Utc } from "@tento-chrono";
import { GhostImage } from "@tento-ui/ui/ghost-image";
import { Div } from "@tento-ui/component-builtin";
import { Typography } from "@tento-ui/ui/typography";
import { LonaIcons, DragIcon } from "@tento-ui/ui/icons";
import { type TasksClient } from "@tento-lona";
import { type TaskId, type Actor, Patch } from "@tento-lona/sheets";
import { reportWriteError } from "../../core/app/errors";

const NAME_MENU_ITEMS: Anchor.MenuItem<NameMenuId>[] = [
  { id: "edit", label: "Rename" },
  { id: "delete", label: "Delete" },
];

const STATUS_MENU_ITEMS: Anchor.MenuItem<StatusMenuId>[] = [
  { id: "completed", label: "Mark as Completed" },
  { id: "inProgress", label: "Mark as In Progress" },
  { id: "planned", label: "Mark as Planned" },
  { id: "autoScheduled", label: "Mark as Auto-Scheduled" },
];

function setNameFieldEditingState(
  $editableText: EditableText,
  editing: boolean,
): void {
  $editableText.toggleEditable(editing);
  $editableText.toggleAttribute("inert", !editing);
}

/** Task identity last bound to each recycled name field, so a same-task
 *  rebind can preserve an in-progress edit (see guard in `bind`). */
const NAME_FIELD_TASK = new WeakMap<EditableText, string>();

function setRowContextMenuOpen($row: HTMLElement, open: boolean): void {
  $row.toggleAttribute("context-menu-open", open);
}

export function createNameColumn(
  onSelect: (
    task: TaskRowItem,
    item: Anchor.MenuItem<NameMenuId>,
    $editableText: EditableText,
  ) => void,
  onEditingFinished: (
    task: TaskRowItem,
    newTitle: Option<string>,
    $editableText: EditableText,
  ) => Promise<void>,
  onMenuOpened?: ($row: HTMLElement) => void,
  onMenuClosed?: ($row: HTMLElement) => void,
): Table.Column.Spec<TaskRowItem, Anchor> {
  return {
    id: "name",
    label: "Name",
    primary: true,
    minWidth: 160,
    sortComparator: (a, b) => (a.title ?? "").localeCompare(b.title ?? ""),
    make: () => {
      const $editableText = EditableText.makeWith("", () => {}, {
        placeholder: "Untitled",
        withOutline: false,
      });
      $editableText.classList.add("attr-name");
      $editableText.style.cursor = "pointer";
      setNameFieldEditingState($editableText, false);

      const $anchor = Anchor.makeWith($editableText, {
        contentProvider: (_point) => {
          const onDismiss = () => {};
          const builder = ContextMenu.builder();
          for (const item of NAME_MENU_ITEMS) {
            builder.cellRow(item.label, item.icon ?? null, (state) => {
              state.shouldDismiss = true;
            });
          }
          return {
            $content: builder.build({ onDismiss }),
            onDismiss,
            dismissOnClickOutside: true,
          };
        },
        ariaHasPopup: "menu",
      });
      $anchor.classList.add("attr-name");
      $anchor.assignStyles({
        minHeight: "24px",
        display: "flex",
        alignItems: "center",
        cursor: "pointer",
        "--padding-inline": "none",
      });
      return $anchor;
    },
    bind: ($anchor, task: TaskRowItem, $row) => {
      const $editableText = $anchor.firstElementChild as EditableText;
      if (!$editableText) return;

      $anchor.bind($editableText, {
        contentProvider: (_point) => {
          const onDismiss = () => {
            onMenuClosed?.($row);
          };
          onMenuOpened?.($row);
          const builder = ContextMenu.builder();
          for (const item of NAME_MENU_ITEMS) {
            builder.cellRow(item.label, item.icon ?? null, (state) => {
              onSelect(task, item, $editableText);
              state.shouldDismiss = true;
            });
          }
          return {
            $content: builder.build({ onDismiss }),
            onDismiss,
            dismissOnClickOutside: true,
          };
        },
        ariaHasPopup: "menu",
      });

      // A table rebind (own-write echo, background refresh) must not
      // clobber an in-progress title edit — skip the text rebind only
      // when this recycled field still shows the SAME task and is
      // actively being edited. A different task always rebinds, so
      // row recycling can't leak the previous task's draft.
      const sameTask = NAME_FIELD_TASK.get($editableText) === task.id.toString();
      NAME_FIELD_TASK.set($editableText, task.id.toString());
      const editing =
        $editableText.editable() && $editableText.matches(":focus-within");
      if (!(sameTask && editing)) {
        $editableText.bindText(task.title ?? "Untitled");
      }
      $editableText.controller.onEditingFinished = async (newTitle) => {
        await onEditingFinished(task, newTitle, $editableText);
      };
      $editableText.controller.onEnterKey = () => {
        $editableText.blur();
      };

      const keydownHandler = (e: KeyboardEvent) => {
        if (e.key === "Escape") {
          e.preventDefault();
          e.stopPropagation();
          if (task.isPending) {
            // For pending tasks, clear text so onEditingFinished will delete it
            $editableText.bindText("");
          } else {
            // For existing tasks, restore original text
            $editableText.bindText(task.title ?? "Untitled");
          }
          $editableText.disableEditing();
        }
      };
      $editableText.removeEventListener("keydown", keydownHandler);
      $editableText.addEventListener("keydown", keydownHandler);
    },
    layout($e, layoutStyle) {
      const $et = $e.firstElementChild as EditableText;
      $et.assignStyles({
        width: layoutStyle === "inline" ? "100%" : "",
        "--placeholder-right": layoutStyle === "inline" ? "none" : "",
      });
    },
  };
}

export function createStatusColumn(
  onSelect: (
    task: TaskRowItem,
    item: Anchor.MenuItem<StatusMenuId>,
    $indicator: TaskStatusIndicator,
  ) => void,
  onMenuOpened?: ($row: HTMLElement) => void,
  onMenuClosed?: ($row: HTMLElement) => void,
): Table.Column.Spec<TaskRowItem, Anchor> {
  return {
    id: "status",
    label: "Status",
    width: 24,
    minWidth: 24,
    showLabel: false,
    sortComparator: (a, b) => {
      const aVal = a.status
        ? TaskPresentation.statusRank(a.status)
        : -1;
      const bVal = b.status
        ? TaskPresentation.statusRank(b.status)
        : -1;
      return aVal - bVal;
    },
    make: () => {
      const $indicator = TaskStatusIndicator.make();

      const $anchor = Anchor.makeWith($indicator, {
        contentProvider: (_point) => {
          const onDismiss = () => {};
          const builder = ContextMenu.builder();
          for (const item of STATUS_MENU_ITEMS) {
            builder.cellRow(item.label, item.icon ?? null, (state) => {
              state.shouldDismiss = true;
            });
          }
          return {
            $content: builder.build({ onDismiss }),
            onDismiss,
            dismissOnClickOutside: true,
          };
        },
        ariaHasPopup: "menu",
      });
      $anchor.assignStyles({
        minWidth: "24px",
        height: "100%",
        display: "flex",
        alignItems: "center",
      });
      return $anchor;
    },
    bind: ($anchor, task: TaskRowItem, $row) => {
      const $indicator = $anchor.firstElementChild as TaskStatusIndicator;
      if (!$indicator) return;

      $anchor.bind($indicator, {
        contentProvider: (_point) => {
          const onDismiss = () => {
            onMenuClosed?.($row);
          };
          onMenuOpened?.($row);
          const builder = ContextMenu.builder();
          for (const item of STATUS_MENU_ITEMS) {
            builder.cellRow(item.label, item.icon ?? null, (state) => {
              onSelect(task, item, $indicator);
              state.shouldDismiss = true;
            });
          }
          return {
            $content: builder.build({ onDismiss }),
            onDismiss,
            dismissOnClickOutside: true,
          };
        },
        ariaHasPopup: "menu",
      });

      $indicator.bind(task.status);
    },
  };
}

export function createDateColumn(
  onSelect: (
    task: TaskRowItem,
    item: Anchor.MenuItem<TaskDateDisplay.DateMenuId>,
    $anchor: Anchor,
    $date: TaskDateDisplay,
  ) => void,
  onMenuOpened?: ($row: HTMLElement) => void,
  onMenuClosed?: ($row: HTMLElement) => void,
): Table.Column.Spec<TaskRowItem, Anchor> {
  return {
    id: "dueDate",
    label: "Due Date",
    width: 120,
    sortComparator: (a, b) => {
      if (!a.dueDate && !b.dueDate) return 0;
      if (!a.dueDate) return 1;
      if (!b.dueDate) return -1;
      return a.dueDate.mse - b.dueDate.mse;
    },
    make: () => {
      const $date = TaskDateDisplay.make();
      $date.style.minWidth = "0px";

      const $anchor = Anchor.makeWith($date, {
        contentProvider: (_point) => {
          const onDismiss = () => {};
          const builder = ContextMenu.builder();
          for (const item of TaskDateDisplay.MENU_ITEMS) {
            builder.cellRow(item.label, item.icon ?? null, (state) => {
              state.shouldDismiss = true;
            });
          }
          return {
            $content: builder.build({ onDismiss }),
            onDismiss,
            dismissOnClickOutside: true,
          };
        },
        ariaHasPopup: "menu",
      });
      $anchor.assignStyles({
        height: "100%",
        display: "flex",
        alignItems: "center",
      });
      return $anchor;
    },
    bind: ($anchor, task: TaskRowItem, $row) => {
      const $date = $anchor.firstElementChild as TaskDateDisplay;
      if (!$date) return;

      $anchor.bind($date, {
        contentProvider: (_point) => {
          const onDismiss = () => {
            onMenuClosed?.($row);
          };
          onMenuOpened?.($row);
          const builder = ContextMenu.builder();
          for (const item of TaskDateDisplay.MENU_ITEMS) {
            builder.cellRow(item.label, item.icon ?? null, (state) => {
              if (item.id !== "pick") {
                TaskDateDisplay.applyMenuItem($date, item.id);
              }
              onSelect(task, item, $anchor, $date);
              state.shouldDismiss = true;
            });
          }
          return {
            $content: builder.build({ onDismiss }),
            onDismiss,
            dismissOnClickOutside: true,
          };
        },
        ariaHasPopup: "menu",
      });

      const isCompleted = task.status === "completed";
      $anchor.toggleAttribute("completed", isCompleted);
      $date.bind(task.dueDate, { showOverdue: !isCompleted });
    },
    layout($e, layoutStyle) {
      const isInline = layoutStyle === "inline";
      const isCompleted = $e.hasAttribute("completed");
      $e.children[0]?.toggleAttribute("inline", isInline);
      $e.style.display = isInline && isCompleted ? "none" : "flex";
    },
  };
}

export function createPriorityColumn(
  onSelect: (
    task: TaskRowItem,
    item: Anchor.MenuItem<TaskPriorityIndicator.PriorityMenuId>,
    $indicator: TaskPriorityIndicator,
  ) => void,
  onMenuOpened?: ($row: HTMLElement) => void,
  onMenuClosed?: ($row: HTMLElement) => void,
): Table.Column.Spec<TaskRowItem, Anchor> {
  return {
    id: "priority",
    label: "Priority",
    width: 80,
    sortComparator: (a, b) => {
      const aVal = a.importance
        ? TaskPresentation.importanceRank(a.importance)
        : 999;
      const bVal = b.importance
        ? TaskPresentation.importanceRank(b.importance)
        : 999;
      return aVal - bVal;
    },
    make: () => {
      const $indicator = TaskPriorityIndicator.make();

      const $anchor = Anchor.makeWith($indicator, {
        contentProvider: (_point) => {
          const onDismiss = () => {};
          const builder = ContextMenu.builder();
          for (const item of TaskPriorityIndicator.MENU_ITEMS) {
            builder.cellRow(item.label, item.icon ?? null, (state) => {
              state.shouldDismiss = true;
            });
          }
          return {
            $content: builder.build({ onDismiss }),
            onDismiss,
            dismissOnClickOutside: true,
          };
        },
        ariaHasPopup: "menu",
      });
      $anchor.assignStyles({
        height: "100%",
        display: "flex",
        alignItems: "center",
      });
      return $anchor;
    },
    bind: ($anchor, task: TaskRowItem, $row) => {
      const $indicator = $anchor.firstElementChild as TaskPriorityIndicator;
      if (!$indicator) return;

      $anchor.bind($indicator, {
        contentProvider: (_point) => {
          const onDismiss = () => {
            onMenuClosed?.($row);
          };
          onMenuOpened?.($row);
          const builder = ContextMenu.builder();
          for (const item of TaskPriorityIndicator.MENU_ITEMS) {
            builder.cellRow(item.label, item.icon ?? null, (state) => {
              onSelect(task, item, $indicator);
              state.shouldDismiss = true;
            });
          }
          return {
            $content: builder.build({ onDismiss }),
            onDismiss,
            dismissOnClickOutside: true,
          };
        },
        ariaHasPopup: "menu",
      });

      const isCompleted = task.status === "completed";
      $anchor.toggleAttribute("completed", isCompleted);
      $indicator.bind(task.importance);
    },
    layout($e, layoutStyle) {
      const isInline = layoutStyle === "inline";
      const isCompleted = $e.hasAttribute("completed");
      $e.style.display = isInline && isCompleted ? "none" : "flex";
    },
  };
}

function createTaskIdColumn(): Table.Column.Spec<TaskRowItem, HTMLElement> {
  return {
    id: "taskId",
    label: "ID",
    width: 56,
    sortComparator: (a, b) => (a.displayNumber ?? 0) - (b.displayNumber ?? 0),
    make: () => {
      const $el = Typography.p("");
      $el.assignStyles({
        height: "100%",
        display: "flex",
        alignItems: "center",
        color: "var(--text-sec)",
      });
      return $el;
    },
    bind: ($el, task) => {
      $el.textContent = task.displayId ?? "";
    },
    layout($el, layoutStyle) {
      $el.style.display = layoutStyle === "inline" ? "none" : "flex";
    },
  };
}

export function createDragColumn(): Table.Column.Spec<TaskRowItem, HTMLElement> {
  return {
    id: "drag-handle",
    label: "",
    width: 12,
    minWidth: 12,
    showLabel: false,
    make: () => {
      const $icon = LonaIcons.make(DragIcon, {
        sizePx: 12,
        color: "var(--text-ter)",
      });
      $icon.style.cursor = "grab";
      $icon.style.flexShrink = "0";
      $icon.style.opacity = "0";
      $icon.style.transition = "opacity 0.3s ease";
      return $icon;
    },
    bind: ($icon, task, $row) => {
      let dragging = false;
      $row.onpointerenter = () => {
        $icon.style.opacity = "1";
      };
      $row.onpointerleave = () => {
        $icon.style.opacity = "0";
      };

      $icon.onpointerdown = (ev: PointerEvent) => {
        if (ev.button !== 0) return;
        ev.stopPropagation();
        ev.preventDefault();

        const onMove = () => {
          if (dragging) return;
          dragging = true;
          document.body.style.userSelect = "none";
          DragTaskState.start(task.id);

          const $label = Div.make();
          $label.textContent = task.title ?? "Untitled";
          $label.style.padding = "4px 8px";
          $label.style.background = "var(--bg)";
          $label.style.borderRadius = "4px";
          $label.style.border = "1px solid var(--divider)";
          $label.style.color = "var(--text-pri)";
          $label.style.fontSize = "var(--text-14px)";
          $label.style.whiteSpace = "nowrap";
          GhostImage.setGhostImage($label);
        };

        const onUp = () => {
          if (dragging) {
            GhostImage.setGhostImage(null);
            document.body.style.userSelect = "";
            // Use setTimeout (macro task) — not Promise.resolve (microtask) —
            // because browsers run microtask checkpoints between event handler
            // callbacks within the same dispatch, clearing state before the
            // gesture's guard() runs.
            setTimeout(() => {
              if (DragTaskState.isDragging()) DragTaskState.end();
            }, 0);
          }
          dragging = false;
          document.removeEventListener("pointermove", onMove, {
            capture: true,
          });
          document.removeEventListener("pointerup", onUp, { capture: true });
        };

        // Use capture so these fire even when elements call stopPropagation.
        document.addEventListener("pointermove", onMove, { capture: true });
        document.addEventListener("pointerup", onUp, { capture: true });
      };
    },
  };
}

const TASK_CONTEXT_MENU_ID = "task-context-menu";

export namespace TaskTableColumns {
  export type Actions = {
    tasks: TasksClient.Api;
    /** Self-echo actor stamped on this table's task writes so the
     *  table (and sheet task cells) can distinguish its own echoes. */
    actor: Actor;
    removePending(taskId: TaskId): void;
    onChanged(): void;
  };

  export function all(
    actions: Actions,
    onSidebarFocusChanged?: (contextMenuId: string, focused: boolean) => void,
  ): Table.Column.Spec<TaskRowItem, any>[] {
    let activeMenuRow: Option<HTMLElement> = null;
    const clearActiveMenuRow = () => {
      if (!activeMenuRow) return;
      setRowContextMenuOpen(activeMenuRow, false);
      activeMenuRow = null;
    };
    const onMenuOpened = ($row: HTMLElement) => {
      if (activeMenuRow !== $row) {
        clearActiveMenuRow();
      }
      activeMenuRow = $row;
      setRowContextMenuOpen($row, true);
      onSidebarFocusChanged?.(TASK_CONTEXT_MENU_ID, true);
    };
    const onMenuClosed = (_$row: HTMLElement) => {
      clearActiveMenuRow();
      onSidebarFocusChanged?.(TASK_CONTEXT_MENU_ID, false);
    };
    return [
      createDragColumn(),
      createStatusColumn(
        (task, item, $indicator) => {
          if (task.isPending) return;
          $indicator.bind(item.id);
          void actions.tasks.update(task, { status: item.id }, actions.actor).catch((e: unknown) => {
            reportWriteError("update the task", e);
            // Re-list from the rolled-back mirror: undoes `$indicator.bind`.
            actions.onChanged();
          });
        },
        onMenuOpened,
        onMenuClosed,
      ),
      createNameColumn(
        async (task, item, $editableText) => {
          switch (item.id) {
            case "edit":
              setNameFieldEditingState($editableText, true);
              $editableText.enableEditing(true, true);
              break;
            case "delete": {
              if (task.isPending) {
                actions.removePending(task.id);
                actions.onChanged();
                return;
              }
              const result = await Prompt.show(
                Prompt.Props.makeDelete(
                  `Are you sure you want to delete the task "${task.title || "Untitled"}"? This action cannot be undone.`,
                ),
              );
              if (result.didComplete()) {
                try {
                  await actions.tasks.delete(task, actions.actor);
                } catch (e) {
                  reportWriteError("delete the task", e);
                  return;
                }
                actions.onChanged();
              }
              break;
            }
          }
        },
        async (task, newTitle, $editableText) => {
          setNameFieldEditingState($editableText, false);

          if (task.isPending) {
            if (newTitle?.trim()) {
              actions.removePending(task.id);
              try {
                await actions.tasks.create({ title: newTitle, description: null, dueDate: null, eventId: null, importance: null, position: null, scheduledEnd: null, scheduledStart: null, status: null }, actions.actor);
              } catch (e) {
                reportWriteError("create the task", e);
              }
              actions.onChanged();
            } else {
              actions.removePending(task.id);
              actions.onChanged();
            }
          } else {
            if (newTitle && newTitle !== task.title) {
              void actions.tasks.update(task, { title: newTitle }, actions.actor).catch((e: unknown) => {
                reportWriteError("rename the task", e);
                // Re-list from the rolled-back mirror: the field keeps the refused text otherwise.
                actions.onChanged();
              });
            }
          }
        },
        onMenuOpened,
        onMenuClosed,
      ),
      createTaskIdColumn(),
      createDateColumn(
        (task, item, _$cell, $date) => {
          if (item.id === "pick") {
            TaskDateDisplay.showPickerModal(
              task.dueDate ? task.dueDate.ndt.date : null,
              async (selectedDate) => {
                if (selectedDate) {
                  const dueDate = new DateTime(
                    new NaiveDateTime(selectedDate),
                    Utc,
                  );
                  $date.bind(dueDate);
                  void actions.tasks.update(task, { dueDate }, actions.actor).catch((e: unknown) => {
                    reportWriteError("update the task", e);
                    // Re-list from the rolled-back mirror: undoes `$date.bind`.
                    actions.onChanged();
                  });
                }
              },
            );
            return;
          }

          const menuDueDate = TaskDateDisplay.dateForMenuItem(item.id);
          void actions.tasks.update(task, { dueDate: menuDueDate ?? Patch.clear }, actions.actor).catch((e: unknown) => {
            reportWriteError("update the task", e);
            actions.onChanged();
          });
        },
        onMenuOpened,
        onMenuClosed,
      ),
      createPriorityColumn(
        (task, item, $indicator) => {
          // `Patch.clear` clears the priority; omitting it leaves it unchanged.
          const importance = item.id === "none" ? null : item.id;
          $indicator.bind(importance);
          void actions.tasks.update(task, { importance: importance ?? Patch.clear }, actions.actor).catch((e: unknown) => {
            reportWriteError("update the task", e);
            // Re-list from the rolled-back mirror: undoes `$indicator.bind`.
            actions.onChanged();
          });
        },
        onMenuOpened,
        onMenuClosed,
      ),
    ];
  }
}
